import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Public as PublicIcon,
  QrCode as QrCodeIcon,
  Email as EmailIcon,
  Star as StarIcon,
  Payment as PaymentIcon,
  Visibility as VisibilityIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';

const iconMap = {
  ScheduleIcon: <ScheduleIcon sx={{ fontSize: { xs: 28, md: 32 }, color: '#212121' }} />,
  PublicIcon: <PublicIcon sx={{ fontSize: { xs: 28, md: 32 }, color: '#212121' }} />,
  QrCodeIcon: <QrCodeIcon sx={{ fontSize: { xs: 28, md: 32 }, color: '#212121' }} />,
  EmailIcon: <EmailIcon sx={{ fontSize: { xs: 28, md: 32 }, color: '#212121' }} />,
  StarIcon: <StarIcon sx={{ fontSize: { xs: 28, md: 32 }, color: '#212121' }} />,
  PaymentIcon: <PaymentIcon sx={{ fontSize: { xs: 28, md: 32 }, color: '#212121' }} />,
  VisibilityIcon: <VisibilityIcon sx={{ fontSize: { xs: 28, md: 32 }, color: '#212121' }} />
};

const iconBackgrounds = [
  'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
  'linear-gradient(135deg, #e8f5e8 0%, #d4f4d4 100%)',
  'linear-gradient(135deg, #e8f4fd 0%, #d1ecf1 100%)',
  'linear-gradient(135deg, #fff2e8 0%, #ffe4d1 100%)',
  'linear-gradient(135deg, #fef7e8 0%, #fdeaa7 100%)',
  'linear-gradient(135deg, #f0e8ff 0%, #e1d4f7 100%)',
  'linear-gradient(135deg, #ffe8f0 0%, #ffd1e1 100%)'
];

const getFeatures = (t) => [
  {
    icon: 'ScheduleIcon',
    title: t('features.smartCalendar'),
    description: t('features.smartCalendarDesc')
  },
  {
    icon: 'PublicIcon',
    title: t('features.publicProfile'),
    description: t('features.publicProfileDesc')
  },
  {
    icon: 'QrCodeIcon',
    title: t('features.customQR'),
    description: t('features.customQRDesc')
  },
  {
    icon: 'EmailIcon',
    title: t('features.autoReminders'),
    description: t('features.autoRemindersDesc')
  },
  {
    icon: 'StarIcon',
    title: t('features.seoBoost'),
    description: t('features.seoBoostDesc')
  },
  {
    icon: 'PaymentIcon',
    title: t('features.securePayment'),
    description: t('features.securePaymentDesc')
  },
  {
    icon: 'VisibilityIcon',
    title: t('features.maxVisibility'),
    description: t('features.maxVisibilityDesc')
  }
];

const HowItWorks = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const features = getFeatures(t);

  return (
    <Box sx={{
      backgroundColor: '#f5f5dc',
      py: { xs: 4, md: 6 },
      textAlign: 'center',
      color: 'black'
    }}>
      <Container maxWidth="lg">
        {/* Main Title */}
        <Box textAlign="center" mb={{ xs: 3, md: 4 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 700,
              color: '#212121',
              lineHeight: { xs: 1.2, md: 1.1 },
              mb: 1,
              letterSpacing: '-0.01em'
            }}
          >
            {t('howItWorks.howItWorks')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#666',
              fontSize: { xs: '1rem', md: '1.1rem' },
              fontWeight: 400,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.5
            }}
          >
            {t('howItWorks.discoverFeatures')}
          </Typography>
        </Box>

        {/* Features Section */}
        <Box>
          <Typography
            variant="h4"
            component="h3"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: '#212121',
              mb: { xs: 3, md: 4 },
              fontSize: { xs: '1.3rem', md: '1.6rem' }
            }}
          >
            {t('howItWorks.keyFeatures')}
          </Typography>

          {/* Features List - Compact Horizontal Layout */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1.5, md: 2 },
              maxWidth: '900px',
              mx: 'auto'
            }}
          >
            {features.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 2, md: 3 },
                  p: { xs: 1.5, md: 2 },
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.8)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: `slideInLeft 0.6s ease-out ${index * 0.1}s both`,
                  '@keyframes slideInLeft': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateX(-20px)'
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateX(0)'
                    }
                  },
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.9)',
                    transform: 'translateX(4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.08)'
                  }
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    width: { xs: 40, md: 48 },
                    height: { xs: 40, md: 48 },
                    borderRadius: 2,
                    background: iconBackgrounds[index % iconBackgrounds.length],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(255,255,255,0.8)'
                  }}
                >
                  <Box sx={{ '& svg': { fontSize: { xs: '1.2rem', md: '1.4rem' }, color: '#212121' } }}>
                    {iconMap[feature.icon]}
                  </Box>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    component="h4"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '0.95rem', md: '1.1rem' },
                      color: '#212121',
                      mb: 0.5,
                      lineHeight: 1.3
                    }}
                  >
                    {feature.title}
                  </Typography>
                  
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: '0.8rem', md: '0.9rem' },
                      color: '#666',
                      lineHeight: 1.4
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorks;